/**
 * Chapter 8 — verdicts: six days of CLEARANCE-CLICKS on the REAL Live card
 * (pass → pass → a FAIL burns and stops → next list → depleted skip →
 * paused), with the guards above and the verdict's own action words.
 * Reduced motion shows the six days as a strip.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC } from 'react';

import { Badge } from '@/components/ui';
import { DEFAULT_VERDICT } from '@/features/drop-generator/components/editor/state/satellites/verdict-draft';
import { ContainerLiveCard } from '@/features/drop-generator/components/live/ContainerLiveCard';
import { actionCopy } from '@/features/drop-generator/components/live/lists/verdict-copy';

import { DEMO_RULE_SENTENCE, DEMO_VERDICT_DAYS, clearanceOnDay, type DemoVerdictDay } from '../../fixtures';
import { FrameStack } from '../../motion/FrameStack';
import { Illustration } from '../../motion/Illustration';
import { Node } from '../../motion/Node';

const CYCLE = 21;
const TONE: Record<string, 'success' | 'destructive' | 'secondary'> = { PASS: 'success', FAIL: 'destructive' };
const noop = () => undefined;

const Guards: FC = () => (
  <div className="flex flex-wrap items-center gap-1.5 text-xs">
    <Node at={0}>{DEMO_RULE_SENTENCE}</Node>
    <Node at={1}>waits for {DEFAULT_VERDICT.minSampleSent} SMS</Node>
    <Node at={2}>and {DEFAULT_VERDICT.evaluationDelayMin} min after the last send</Node>
    <Node at={3}>a FAIL burns the list</Node>
  </div>
);

const Day: FC<{ d: DemoVerdictDay; index: number }> = ({ d, index }) => (
  <div className="flex flex-col gap-1">
    <p className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
      <span className="rounded bg-muted px-1.5 py-0.5 font-mono text-foreground">{d.day}</span> {d.note}
    </p>
    <ContainerLiveCard
      container={clearanceOnDay(index + 1)}
      verdicts={d.verdict ? [d.verdict] : undefined}
      onResetPool={index === DEMO_VERDICT_DAYS.length - 1 ? noop : undefined}
    />
  </div>
);

const Strip: FC = () => (
  <div className="grid gap-2 text-xs md:grid-cols-3">
    {DEMO_VERDICT_DAYS.map((d) => (
      <div key={d.day} className="flex flex-col gap-1 rounded border border-border/60 bg-background p-2">
        <span className="font-mono font-semibold">{d.day}</span>
        {d.verdict ? <Badge tone={TONE[d.verdict.verdict] ?? 'secondary'}>{d.verdict.verdict.toLowerCase()}</Badge> : <Badge tone="warning">paused</Badge>}
        <span className="text-muted-foreground">{d.verdict ? actionCopy(d.verdict) || d.note : d.note}</span>
      </div>
    ))}
  </div>
);

export const VerdictsIllustration: FC = () => (
  <Illustration cycleSeconds={CYCLE} finalFrame={<div className="flex flex-col gap-3"><Guards /><Strip /></div>}>
    <Guards />
    <div className="mt-3">
      <FrameStack cycleSeconds={CYCLE} frames={DEMO_VERDICT_DAYS.map((d, i) => <Day key={d.day} d={d} index={i} />)} />
    </div>
  </Illustration>
);
